import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { readFeatureFlagOverrides } from "./featureFlagOverrides";
import {
  DEFAULT_FEATURE_FLAGS,
  featureFlagMemoDeps,
  pickFeatureFlags,
  type FeatureFlags,
} from "./featureFlags";

export type { FeatureFlags } from "./featureFlags";
export { FEATURE_FLAG_KEYS } from "./featureFlags";

export const fetchRemoteFeatureFlags = async (
  token?: string | null,
): Promise<FeatureFlags> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 3000);
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const response = await fetch("/api/features/", {
      headers,
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Feature flags request failed (${response.status})`);
    }
    const data = (await response.json()) as Record<string, unknown>;
    return pickFeatureFlags(data);
  } finally {
    clearTimeout(timer);
  }
};

export const useFeatureFlags = () => {
  const { user, loading: authLoading, getIdToken } = useAuth();
  const { data: remoteFlags, isLoading, isError } = useQuery({
    queryKey: ["featureFlags", "remote", user?.email ?? null],
    enabled: !authLoading,
    queryFn: async () => {
      const token = await getIdToken();
      return fetchRemoteFeatureFlags(token);
    },
    staleTime: 60_000,
    retry: 1,
    // A failed fetch has no data, so a remount would flip the query back to
    // `pending`. Gates that render nothing while loading would then unmount
    // their children, which remount on the next error… Don't refetch on mount.
    retryOnMount: false,
  });

  const resolvedRemoteFlags = remoteFlags ?? DEFAULT_FEATURE_FLAGS;
  const overrides = readFeatureFlagOverrides();

  const flags = useMemo(
    (): FeatureFlags => ({ ...resolvedRemoteFlags, ...overrides }),
    featureFlagMemoDeps(resolvedRemoteFlags, overrides),
  );

  return {
    flags,
    remoteFlags: resolvedRemoteFlags,
    overrides,
    // After a failed fetch we fall back to defaults + overrides rather than
    // keeping gated consumers blank.
    isLoading: (authLoading || isLoading) && !isError,
  };
};
